import axiosConfigAuth from "./axiosConfigAuth";

/**
 * Admin API Service
 * Provides functions for admin user management, wallet adjustments, and audit logs.
 * Uses axiosConfigAuth instance for authenticated requests.
 */

//#region "USER MANAGEMENT"

/**
 * Gets paginated list of non-admin users with optional search.
 * @param {string} search - Optional search term for email, name, or document
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20, max: 100)
 * @returns {Promise<{items: Array, totalCount: number, page: number, pageSize: number, totalPages: number}>}
 */
export function getUserList(search = null, page = 1, pageSize = 20) {
  return new Promise((resolve, reject) => {
    const params = { page, pageSize };
    if (search) {
      params.search = search;
    }

    axiosConfigAuth
      .get("/api/v1/admin/users", { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets details for a specific non-admin user.
 * @param {number} userId - The user ID to retrieve
 * @returns {Promise<{id: number, name: string, email: string, phone: string, document: string, active: boolean, dtCreated: string, walletBalance: number}>}
 */
export function getUserDetails(userId) {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .get(`/api/v1/admin/users/${userId}`)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}


/**
 * Activates a user account by setting Active flag to true.
 * @param {number} userId - The user ID to activate
 * @returns {Promise<{message: string, userId: number, active: boolean}>}
 */
export function activateUser(userId) {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .put(`/api/v1/admin/users/${userId}/activate`)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Deactivates a user account by setting Active flag to false.
 * @param {number} userId - The user ID to deactivate
 * @returns {Promise<{message: string, userId: number, active: boolean}>}
 */
export function deactivateUser(userId) {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .put(`/api/v1/admin/users/${userId}/deactivate`)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Performs a hard delete of a user account.
 * @param {number} userId - The user ID to delete
 * @param {string} reason - Optional reason for deletion
 * @returns {Promise<{message: string, userId: number, userLogId: number}>}
 */
export function deleteUser(userId, reason = null) {
  return new Promise((resolve, reject) => {
    const params = {};
    if (reason) {
      params.reason = reason;
    }

    axiosConfigAuth
      .delete(`/api/v1/admin/users/${userId}`, { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

//#endregion

//#region "WALLET MANAGEMENT"

/**
 * Adjusts a user's wallet balance (credit or debit).
 * @param {number} userId - The user ID whose wallet to adjust
 * @param {number} amount - Positive for credit, negative for debit
 * @param {string} reason - Reason for adjustment (minimum 10 characters)
 * @returns {Promise<{walletId: number, previousBalance: number, newBalance: number, adjustmentAmount: number, reason: string, timestamp: string}>}
 */
export function adjustWalletBalance(userId, amount, reason) {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .post("/api/v1/admin/wallets/adjust", {
        userId,
        amount,
        reason,
      })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets wallet adjustment history for a specific user.
 * @param {number} userId - The user ID to get adjustment history for
 * @param {Date|string} fromDate - Optional start date filter
 * @param {Date|string} toDate - Optional end date filter
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20, max: 100)
 * @returns {Promise<{items: Array, totalCount: number, page: number, pageSize: number, totalPages: number}>}
 */
export function getUserWalletHistory(userId, fromDate = null, toDate = null, page = 1, pageSize = 20) {
  return new Promise((resolve, reject) => {
    const params = { page, pageSize };
    if (fromDate) {
      params.from = fromDate instanceof Date ? fromDate.toISOString() : fromDate;
    }
    if (toDate) {
      params.to = toDate instanceof Date ? toDate.toISOString() : toDate;
    }

    axiosConfigAuth
      .get(`/api/v1/admin/wallets/${userId}/history`, { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

//#endregion

//#region "AUDIT LOGS"

/**
 * Gets audit logs with filtering options.
 * @param {number} adminId - Optional filter by admin user ID
 * @param {string} actionType - Optional filter by action type (e.g., USER_ACTIVATE, WALLET_CREDIT)
 * @param {Date|string} fromDate - Optional start date filter
 * @param {Date|string} toDate - Optional end date filter
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20, max: 100)
 * @returns {Promise<{items: Array, totalCount: number, page: number, pageSize: number, totalPages: number}>}
 */
export function getAuditLogs(adminId = null, actionType = null, fromDate = null, toDate = null, page = 1, pageSize = 20) {
  return new Promise((resolve, reject) => {
    const params = { page, pageSize };
    if (adminId) {
      params.adminId = adminId;
    }
    if (actionType) {
      params.actionType = actionType;
    }
    if (fromDate) {
      params.from = fromDate instanceof Date ? fromDate.toISOString() : fromDate;
    }
    if (toDate) {
      params.to = toDate instanceof Date ? toDate.toISOString() : toDate;
    }

    axiosConfigAuth
      .get("/api/v1/admin/audit", { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

//#endregion

//#region "USER DOCUMENTS"

/**
 * Gets paginated list of all user documents with optional filters.
 * @param {Object} filters - Filter options
 * @param {string} filters.search - Search term for user name, email, or document type
 * @param {string} filters.documentType - Filter by document type
 * @param {string} filters.status - Filter by document status (Pendente, Aprovado, Negado)
 * @param {number} filters.userId - Filter by specific user ID
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20, max: 100)
 * @returns {Promise<{items: Array, totalCount: number, page: number, pageSize: number, totalPages: number}>}
 */
export function getAllUserDocuments(filters = {}, page = 1, pageSize = 20) {
  return new Promise((resolve, reject) => {
    const params = { page, pageSize };
    
    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.documentType) {
      params.documentType = filters.documentType;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.userId) {
      params.userId = filters.userId;
    }

    axiosConfigAuth
      .get("/api/v1/admin/user-docs/all", { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets all documents for a specific user.
 * @param {number} userId - The user ID to get documents for
 * @returns {Promise<Array>} List of user documents
 */
export function getUserDocuments(userId) {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .get(`/api/v1/admin/user-docs/user/${userId}`)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets list of distinct document types in the system.
 * @returns {Promise<Array<string>>} List of document types
 */
export function getDocumentTypes() {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .get("/api/v1/admin/user-docs/types")
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets the proxy URL for viewing a document.
 * @param {number} documentId - The document ID
 * @returns {string} The proxy URL for the document
 */
export function getDocumentProxyUrl(documentId) {
  return `/api/v1/admin/user-docs/proxy/${documentId}`;
}

/**
 * Approve or reject a document (admin). Rejection requires a reason.
 * @param {number} documentId - The document ID
 * @param {string} status - "Aprovado" or "Negado"
 * @param {string} [rejectionReason] - Required when status is "Negado"
 * @returns {Promise<{code: string, message: string, data: Object}>}
 */
export function reviewDocument(documentId, status, rejectionReason = null) {
  return new Promise((resolve, reject) => {
    const body = { status };
    if (status === "Negado" && rejectionReason) {
      body.rejectionReason = rejectionReason;
    }

    axiosConfigAuth
      .put(`/api/v1/admin/user-docs/${documentId}/review`, body)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

//#endregion

//#region "TRANSACTION HISTORY"

/**
 * Gets paginated list of all system transactions with filters.
 * @param {Object} filters - Filter options
 * @param {Date|string} filters.fromDate - Start date filter
 * @param {Date|string} filters.toDate - End date filter
 * @param {string} filters.type - Transaction type: payment, wallet_credit, wallet_debit, transfer
 * @param {string} filters.status - Transaction status filter
 * @param {string} filters.search - Search by ticket, plate, email, or name
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20, max: 100)
 * @returns {Promise<{items: Array, totalCount: number, page: number, pageSize: number, totalPages: number}>}
 */
export function getTransactionHistory(filters = {}, page = 1, pageSize = 20) {
  return new Promise((resolve, reject) => {
    const params = { page, pageSize };
    
    if (filters.fromDate) {
      params.from = filters.fromDate instanceof Date ? filters.fromDate.toISOString() : filters.fromDate;
    }
    if (filters.toDate) {
      params.to = filters.toDate instanceof Date ? filters.toDate.toISOString() : filters.toDate;
    }
    if (filters.type) {
      params.type = filters.type;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.search) {
      params.search = filters.search;
    }

    axiosConfigAuth
      .get("/api/v1/admin/transactions", { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets paginated list of transactions for a specific user.
 * @param {number} userId - The user ID to get transactions for
 * @param {Object} filters - Filter options
 * @param {Date|string} filters.fromDate - Start date filter
 * @param {Date|string} filters.toDate - End date filter
 * @param {string} filters.type - Transaction type: payment, wallet_credit, wallet_debit, transfer
 * @param {string} filters.status - Transaction status filter
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20, max: 100)
 * @returns {Promise<{items: Array, totalCount: number, page: number, pageSize: number, totalPages: number}>}
 */
export function getUserTransactionHistory(userId, filters = {}, page = 1, pageSize = 20) {
  return new Promise((resolve, reject) => {
    const params = { page, pageSize };
    
    if (filters.fromDate) {
      params.from = filters.fromDate instanceof Date ? filters.fromDate.toISOString() : filters.fromDate;
    }
    if (filters.toDate) {
      params.to = filters.toDate instanceof Date ? filters.toDate.toISOString() : filters.toDate;
    }
    if (filters.type) {
      params.type = filters.type;
    }
    if (filters.status) {
      params.status = filters.status;
    }

    axiosConfigAuth
      .get(`/api/v1/admin/transactions/${userId}`, { params })
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

/**
 * Gets detailed information for a specific transaction.
 * @param {string} transactionId - Transaction ID in format "{type}_{id}"
 * @returns {Promise<Object>} Full transaction details
 */
export function getTransactionDetail(transactionId) {
  return new Promise((resolve, reject) => {
    axiosConfigAuth
      .get(`/api/v1/admin/transactions/detail/${transactionId}`)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        reject(error.response?.data || error);
      });
  });
}

//#endregion
